<template>
  <div class="homePage">
    <!-- Start Breadcrumb -->
    <Breadcrumb
      :items="items"
      :search_box="true"
      @search_open="search_model = true"
    />
    <!-- End Breadcrumb -->

    <!-- ==================== STATE 1: LOADING PERMISSIONS ==================== -->
    <div
      v-if="loadingPermissions"
      class="loading-state-wrapper py-12 text-center"
    >
      <v-progress-circular
        indeterminate
        color="#783d73"
        size="64"
        width="5"
      ></v-progress-circular>
      <p class="mt-4 text-muted">جاري التحقق من الصلاحيات وتحميل البيانات...</p>
    </div>

    <!-- ==================== STATE 2: NO PERMISSION SCREEN ==================== -->
    <div v-else-if="!hasStatisticsPermission" class="no-permission-wrapper">
      <div class="no-permission-card">
        <div class="lock-icon-glow">
          <div class="glow-ring"></div>
          <i class="fal fa-shield-alt shield-icon"></i>
          <i class="fal fa-lock lock-icon"></i>
        </div>
        <h2 class="no-permission-title">ليس لديك الصلاحية للدخول</h2>
        <p class="no-permission-text">
          عذرًا، لا تملك الصلاحيات الإدارية الكافية لعرض صفحة الإحصائيات
          (statistics.index). إذا كنت تعتقد أن هذا خطأ، يرجى التواصل مع المسؤول
          العام لمنحك الصلاحية.
        </p>

        <!-- Allowed Module Shortcuts for Restricted Admin -->
        <div
          class="allowed-modules-section mt-6"
          v-if="allowedQuickLinks.length"
        >
          <h4 class="allowed-title mb-3">الوحدات المتاحة لك:</h4>
          <div class="d-flex flex-wrap justify-content-center gap-3">
            <router-link
              v-for="link in allowedQuickLinks"
              :key="link.path"
              :to="link.path"
              class="module-chip"
            >
              <i :class="link.icon"></i>
              <span>{{ link.title }}</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== STATE 3: DASHBOARD CONTENT ==================== -->
    <div v-else>
      <!-- Start Hero Welcome Banner & Filter Controls -->
      <div class="hero-welcome-card mb-6">
        <div class="hero-bg-glows">
          <div class="glow-blob blob-1"></div>
          <div class="glow-blob blob-2"></div>
        </div>
        <div class="hero-content">
          <div class="hero-text-side">
            <div class="hero-badge">
              <span class="pulse-indicator"></span>
              <span>لوحة المؤشرات والتحليلات المباشرة</span>
            </div>
            <h1 class="hero-title">مرحباً بك في لوحة تحكم زوز</h1>
            <p class="hero-subtitle">
              نظرة عامة شاملة على أداء المنصة، المتاجر الرائجة، العملاء،
              السائقين، والطلبات المباشرة.
            </p>
            <div class="hero-actions">
              <button class="hero-btn primary-btn" @click="search_model = true">
                <i class="fal fa-search"></i>
                <span>البحث السريع</span>
              </button>
              <button class="hero-btn secondary-btn" @click="getStatisticsData">
                <i class="fal fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                <span>تحديث البيانات</span>
              </button>
            </div>
          </div>

          <!-- Date Filter Card Inside Hero -->
          <!-- <div class="hero-filter-side">
            <div class="floating-filter-glass">
              <div class="filter-glass-header">
                <i class="fal fa-calendar-alt"></i>
                <span>تصفية الإحصائيات بالمدة</span>
              </div>
              <div class="filter-inputs-grid">
                <div class="filter-field">
                  <label>من تاريخ:</label>
                  <input
                    type="date"
                    v-model="filter_from"
                    class="date-input"
                    :max="filter_to || undefined"
                  />
                </div>
                <div class="filter-field">
                  <label>إلى تاريخ:</label>
                  <input
                    type="date"
                    v-model="filter_to"
                    class="date-input"
                    :min="filter_from || undefined"
                  />
                </div>
              </div>
              <div class="filter-actions-row">
                <button
                  class="apply-filter-btn"
                  @click="applyDateFilter"
                  :disabled="loading"
                >
                  <i class="fal fa-filter"></i>
                  <span>تطبيق الفلتر</span>
                </button>
                <button
                  v-if="filter_from || filter_to"
                  class="reset-filter-btn"
                  @click="resetDateFilter"
                  :disabled="loading"
                  title="إلغاء الفلتر"
                >
                  <i class="fal fa-undo"></i>
                </button>
              </div>
            </div>
          </div> -->
        </div>
      </div>
      <!-- End Hero Welcome Banner -->

      <!-- Validation Error Feedback Alert -->
      <v-alert
        v-if="filter_error"
        type="error"
        dismissible
        dense
        border="left"
        class="mb-6 rounded-lg"
        @input="filter_error = null"
      >
        <div class="d-flex align-items-center">
          <i class="fal fa-exclamation-triangle ml-2"></i>
          <span>{{ filter_error }}</span>
        </div>
      </v-alert>

      <!-- ==================== PENDING ACTIONS SHORTCUTS ==================== -->
      <div
        class="pending-actions-section mb-6"
        v-if="loading || visiblePendingActions.length"
      >
        <div class="section-header mb-3">
          <div class="d-flex align-items-center justify-content-between">
            <h2 class="section-title">
              <i class="fal fa-bell-exclamation title-icon warning-icon"></i>
              إجراءات معلقة تتطلب المتابعة
            </h2>
            <span class="stats-count-badge warning-badge" v-if="!loading">
              {{ visiblePendingActions.length }} إجراءات معلقة
            </span>
          </div>
        </div>

        <!-- Skeleton Loading for Pending Actions -->
        <div class="row" v-if="loading">
          <div
            v-for="n in 3"
            :key="'action-skel-' + n"
            class="col-md-6 col-lg-4 mb-3"
          >
            <v-skeleton-loader
              type="list-item-avatar-two-line"
              class="rounded-xl"
            ></v-skeleton-loader>
          </div>
        </div>

        <!-- Action Items Grid -->
        <div class="row" v-else>
          <div
            v-for="(action, index) in visiblePendingActions"
            :key="action.key || index"
            class="col-md-6 col-lg-4 mb-3"
          >
            <component
              :is="canOpenRoute(action.route_front) ? 'router-link' : 'div'"
              :to="canOpenRoute(action.route_front) ? action.route_front : ''"
              class="pending-action-card-link"
              :class="{ 'clickable-card': canOpenRoute(action.route_front) }"
            >
              <div
                class="pending-action-card"
                :class="getActionCardColorClass(action, index)"
              >
                <div class="action-icon-box">
                  <i :class="getActionIcon(action)"></i>
                </div>
                <div class="action-content-box">
                  <div class="action-title-row">
                    <span class="action-title">{{ action.title }}</span>
                  </div>
                  <div class="action-meta-row">
                    <span class="action-count-pill">
                      {{
                        action.count !== null && action.count !== undefined
                          ? action.count
                          : 0
                      }}
                    </span>
                    <span class="action-status-label">بانتظار المعالجة</span>
                  </div>
                </div>
                <div
                  class="action-arrow"
                  v-if="canOpenRoute(action.route_front)"
                >
                  <i class="fal fa-arrow-left"></i>
                </div>
              </div>
            </component>
          </div>
        </div>
      </div>

      <!-- ==================== SUMMARY STATISTIC CARDS ==================== -->
      <div
        class="summary-stat-cards-section mb-6"
        v-if="loading || visibleStatCards.length"
      >
        <div class="section-header mb-4">
          <div class="d-flex align-items-center justify-content-between">
            <h2 class="section-title">
              <i class="fal fa-chart-pie title-icon"></i>
              المؤشرات الإحصائية الرئيسية
            </h2>
            <span class="stats-count-badge" v-if="!loading">
              {{ visibleStatCards.length }} مؤشرات
            </span>
          </div>
        </div>

        <!-- Skeletons for Stat Cards -->
        <div class="row" v-if="loading">
          <div
            v-for="n in 4"
            :key="'card-skel-' + n"
            class="col-md-6 col-lg-4 col-xl-3 magic-chart-wrapper"
          >
            <v-skeleton-loader
              type="card"
              class="rounded-2xl"
              height="185"
            ></v-skeleton-loader>
          </div>
        </div>

        <!-- Render Stat Cards -->
        <div class="row" v-else>
          <div
            v-for="(item, index) in visibleStatCards"
            :key="item.id || index"
            class="col-md-6 col-lg-4 col-xl-3 magic-chart-wrapper"
          >
            <component
              :is="canOpenRoute(item.route_front) ? 'router-link' : 'div'"
              :to="canOpenRoute(item.route_front) ? item.route_front : ''"
              class="creative-card-link"
              :class="{ 'clickable-card': canOpenRoute(item.route_front) }"
            >
              <div class="creative-stat-card" :style="getCardStyle(index)">
                <div class="card-ambient-glow"></div>
                <div class="card-shimmer"></div>

                <div class="card-top-row">
                  <div class="icon-glass-box">
                    <i :class="getItemIcon(item, index)"></i>
                  </div>
                  <!-- <div
                    class="trend-pill"
                    :class="getItemTrend(item, index).type"
                  >
                    <i :class="getItemTrend(item, index).icon"></i>
                    <span>{{ getItemTrend(item, index).value }}</span>
                  </div> -->
                </div>

                <div class="card-metric-row">
                  <h3 class="stat-number">
                    {{
                      item.count !== null && item.count !== undefined
                        ? item.count
                        : 0
                    }}
                  </h3>
                  <span class="stat-label-text">{{ item.title }}</span>
                </div>

                <div class="card-footer-action">
                  <span>{{
                    canOpenRoute(item.route_front)
                      ? "عرض التفاصيل"
                      : "مؤشر إحصائي"
                  }}</span>
                  <i
                    v-if="canOpenRoute(item.route_front)"
                    class="fal fa-arrow-left arrow-icon"
                  ></i>
                </div>
              </div>
            </component>
          </div>
        </div>
      </div>

      <!-- ==================== GENDER CHARTS SECTION ==================== -->
      <div
        class="gender-charts-section mb-6"
        v-if="canAnyGenderChart && (loading || hasAnyGenderData)"
      >
        <div class="section-header mb-4">
          <div class="d-flex align-items-center justify-content-between">
            <h2 class="section-title">
              <i class="fal fa-venus-mars title-icon"></i>
              توزيع الجنس (السائقين والطلبات)
            </h2>
          </div>
        </div>

        <div class="row">
          <!-- Driver Gender Chart -->
          <div
            v-if="canDriverGenderChart"
            class="col-12"
            :class="canOrderGenderChart ? 'col-lg-6' : 'col-lg-12'"
          >
            <div class="chart-box-card mb-4">
              <div class="chart-header-row">
                <div class="chart-title-wrap">
                  <div class="chart-icon-box driver-icon-bg">
                    <i class="fal fa-id-card"></i>
                  </div>
                  <div>
                    <h3 class="chart-title">توزيع جنس السائقين</h3>
                    <p class="chart-sub">نسبة السائقين المسجلين حسب الجنس</p>
                  </div>
                </div>
                <span class="total-stat-pill">
                  الإجمالي: {{ getGenderTotal(driverGenderData) }}
                </span>
              </div>

              <!-- Chart Content -->
              <div v-if="loading" class="chart-loading-wrap">
                <v-skeleton-loader
                  type="image"
                  height="260"
                ></v-skeleton-loader>
              </div>
              <div v-else class="chart-render-wrap">
                <div class="donut-chart-container">
                  <apexchart
                    type="donut"
                    width="100%"
                    height="270"
                    :options="driverDonutOptions"
                    :series="driverDonutSeries"
                  ></apexchart>
                </div>
                <div class="gender-stats-summary-grid">
                  <div
                    v-for="item in driverGenderData"
                    :key="item.key"
                    class="gender-item-card"
                    :class="item.key"
                  >
                    <span class="gender-key-badge">
                      <i :class="getGenderIcon(item.key)"></i>
                      {{ formatGenderLabel(item.key) }}
                    </span>
                    <span class="gender-count">{{
                      item.count !== null && item.count !== undefined
                        ? item.count
                        : 0
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Gender Chart -->
          <div
            v-if="canOrderGenderChart"
            class="col-12"
            :class="canDriverGenderChart ? 'col-lg-6' : 'col-lg-12'"
          >
            <div class="chart-box-card mb-4">
              <div class="chart-header-row">
                <div class="chart-title-wrap">
                  <div class="chart-icon-box order-icon-bg">
                    <i class="fal fa-shopping-bag"></i>
                  </div>
                  <div>
                    <h3 class="chart-title">توزيع جنس أصحاب الطلبات</h3>
                    <p class="chart-sub">نسبة الطلبات المسجلة حسب جنس العميل</p>
                  </div>
                </div>
                <span class="total-stat-pill">
                  الإجمالي: {{ getGenderTotal(orderGenderData) }}
                </span>
              </div>

              <!-- Chart Content -->
              <div v-if="loading" class="chart-loading-wrap">
                <v-skeleton-loader
                  type="image"
                  height="260"
                ></v-skeleton-loader>
              </div>
              <div v-else class="chart-render-wrap">
                <div class="donut-chart-container">
                  <apexchart
                    type="donut"
                    width="100%"
                    height="270"
                    :options="orderDonutOptions"
                    :series="orderDonutSeries"
                  ></apexchart>
                </div>
                <div class="gender-stats-summary-grid">
                  <div
                    v-for="item in orderGenderData"
                    :key="item.key"
                    class="gender-item-card"
                    :class="item.key"
                  >
                    <span class="gender-key-badge">
                      <i :class="getGenderIcon(item.key)"></i>
                      {{ formatGenderLabel(item.key) }}
                    </span>
                    <span class="gender-count">{{
                      item.count !== null && item.count !== undefined
                        ? item.count
                        : 0
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== LATEST 5 ORDERS ==================== -->
      <div
        class="latest-orders-section mb-6"
        v-if="canOrders && (loading || orders_data !== null)"
      >
        <div class="creative-table-card">
          <div class="table-card-header">
            <div class="table-header-title">
              <div class="header-icon-wrap orders-header-icon">
                <i class="fal fa-boxes"></i>
              </div>
              <div>
                <h3 class="table-title">آخر الطلبات</h3>
                <p class="table-subtitle">
                  أحدث طلبات التوصيل والطرود المسجلة على المنصة
                </p>
              </div>
            </div>
            <div class="table-header-badge" v-if="totalOrdersCount">
              <span>{{ totalOrdersCount }} طلبات حديثة</span>
            </div>
          </div>

          <!-- Orders Tabs Navigation -->
          <div
            class="top-tabs-header"
            v-if="canDeliveryOrders || canPackageOrders"
          >
            <div class="top-tabs-nav">
              <button
                v-if="canDeliveryOrders"
                class="top-tab-btn"
                :class="{ active: activeOrderTab === 'delivery' }"
                @click="activeOrderTab = 'delivery'"
              >
                <div class="top-tab-icon delivery-tab-icon">
                  <i class="fal fa-motorcycle"></i>
                </div>
                <div class="top-tab-text">
                  <span class="top-tab-label">طلبات التوصيل</span>
                  <span class="top-tab-count" v-if="delivery_orders.length">{{
                    delivery_orders.length
                  }}</span>
                </div>
              </button>
              <button
                v-if="canPackageOrders"
                class="top-tab-btn"
                :class="{ active: activeOrderTab === 'package' }"
                @click="activeOrderTab = 'package'"
              >
                <div class="top-tab-icon package-tab-icon">
                  <i class="fal fa-box-check"></i>
                </div>
                <div class="top-tab-text">
                  <span class="top-tab-label">طلبات الطرود</span>
                  <span class="top-tab-count" v-if="package_orders.length">{{
                    package_orders.length
                  }}</span>
                </div>
              </button>
            </div>
          </div>

          <main class="table-body-wrap">
            <v-data-table
              :key="activeOrderTab"
              class="thumb strip creative-data-table"
              :headers="currentOrderHeaders"
              :items="currentOrderItems || []"
              :loading="loading"
              :loading-text="$t('table.loadingData')"
              item-key="id"
              hide-default-footer
            >
              <!-- Index -->
              <template v-slot:[`item.index`]="{ index }">
                <span class="index-badge">{{ index + 1 }}</span>
              </template>

              <!-- Order Number -->
              <template v-slot:[`item.order_number`]="{ item }">
                <span class="order-number-badge">
                  #{{ item.order_number || item.id }}
                </span>
              </template>

              <!-- Client Info -->
              <template v-slot:[`item.client`]="{ item }">
                <div v-if="item.client" class="store-cell-item">
                  <div class="store-img-wrapper">
                    <img
                      @click="show_model_1"
                      class="image store-avatar"
                      :src="
                        item.client.avatar ||
                        require('@/assets/images/Avatars/no-user.png')
                      "
                      @error="
                        $event.target.src = require('@/assets/images/icons/logo.png')
                      "
                    />
                  </div>
                  <div class="store-meta">
                    <span class="store-name">{{
                      item.client.full_name || "عميل"
                    }}</span>
                  </div>
                </div>
                <span v-else class="text-muted">لا يوجد</span>
              </template>

              <!-- Client Phone (Package Orders) -->
              <template v-slot:[`item.client_phone`]="{ item }">
                <span
                  class="phone-cell"
                  v-if="
                    item.client &&
                    (item.client.phone_complete_form || item.client.phone)
                  "
                >
                  <i class="fal fa-phone-alt ml-1"></i>
                  <bdi>{{
                    item.client.phone_complete_form || item.client.phone
                  }}</bdi>
                </span>
                <span
                  v-else-if="item.phone_sender || item.phone_receiver"
                  class="phone-cell"
                >
                  <i class="fal fa-phone-alt ml-1"></i>
                  <bdi>{{ item.phone_sender || item.phone_receiver }}</bdi>
                </span>
                <span v-else class="text-muted">—</span>
              </template>

              <!-- Driver Info -->
              <template v-slot:[`item.driver`]="{ item }">
                <div v-if="item.driver" class="store-cell-item">
                  <div class="store-img-wrapper">
                    <img
                      class="image store-avatar"
                      :src="
                        item.driver.image ||
                        item.driver.avatar ||
                        require('@/assets/images/Avatars/no-user.png')
                      "
                      @error="
                        $event.target.src = require('@/assets/images/icons/logo.png')
                      "
                    />
                  </div>
                  <div class="store-meta">
                    <span class="store-name">{{
                      item.driver.full_name || "سائق"
                    }}</span>
                  </div>
                </div>
                <span v-else class="text-muted">لا يوجد</span>
              </template>

              <!-- Store (delivery_type - for delivery orders) -->
              <template v-slot:[`item.delivery_type`]="{ item }">
                <div v-if="item.restaurant">
                  <p>
                    {{
                      item.restaurant.trad_name ||
                      item.restaurant.full_name ||
                      item.restaurant.name
                    }}
                  </p>
                  <v-chip
                    v-if="item.restaurant.type == 'zoz'"
                    color="#783D73"
                    text-color="#fff"
                    >متجر شريك</v-chip
                  >
                  <v-chip v-else color="blue" text-color="#fff"
                    >متجر جوجل</v-chip
                  >
                </div>
                <div v-else-if="item.resturant_zoz">
                  <p>{{ item.resturant_zoz.trad_name }}</p>
                  <v-chip color="#783D73" text-color="#fff">متجر شريك</v-chip>
                </div>
                <div v-else-if="item.resturant">
                  <p>{{ item.resturant.name }}</p>
                  <v-chip color="blue" text-color="#fff">متجر جوجل</v-chip>
                </div>
                <span v-else class="text-muted">لا يوجد</span>
              </template>

              <!-- Platform (for Package Orders) -->
              <template v-slot:[`item.platform`]="{ item }">
                <v-chip
                  small
                  :color="item.platform === 'web' ? '#783D73' : 'green'"
                  text-color="#fff"
                  v-if="item.platform || item.platform_trans"
                >
                  <i
                    :class="
                      item.platform === 'web'
                        ? 'fal fa-globe ml-1'
                        : 'fal fa-mobile-android ml-1'
                    "
                  ></i>
                  {{
                    item.platform_trans ||
                    (item.platform === "web" ? "موقع الكتروني" : "تطبيق")
                  }}
                </v-chip>
                <span v-else class="text-muted">—</span>
              </template>

              <!-- Status -->
              <template v-slot:[`item.status`]="{ item }">
                <div class="action">
                  <div>
                    <span
                      v-if="
                        item.status == 'driver_rejected' ||
                        item.status == 'client_cancel' ||
                        item.status == 'driver_cancel' ||
                        item.status == 'restaurant_reject'
                      "
                      class="status canceled"
                    >
                      {{ item.status_trans }}
                    </span>
                    <span v-else class="status success">
                      {{ item.status_trans }}
                    </span>
                  </div>
                </div>
              </template>

              <!-- Created At -->
              <template v-slot:[`item.created_at`]="{ item }">
                <span>{{ item.created_at || "—" }}</span>
              </template>

              <!-- Payment Type -->
              <template v-slot:[`item.payment_type`]="{ item }">
                <p>{{ item.payment_type }}</p>
                <div v-if="item.payment_type == 'online'">
                  <v-chip v-if="item.is_paid" color="green" text-color="#fff"
                    >تم الدفع</v-chip
                  >
                  <v-chip v-else color="red" text-color="#fff"
                    >لم يتم الدفع</v-chip
                  >
                </div>
              </template>

              <!-- Additional Details (notes) -->
              <template v-slot:[`item.notes`]="{ item }">
                <div
                  v-if="
                    item.additional_details && item.additional_details.length
                  "
                >
                  <v-chip
                    v-for="(detail, idx) in item.additional_details"
                    :key="idx"
                    :color="getDetailChipColor(detail.key)"
                    text-color="#fff"
                    class="mt-1 mr-1"
                    small
                  >
                    {{ detail.text }}
                  </v-chip>
                </div>
                <div
                  v-else-if="
                    item.fulfillment_type ||
                    item.is_on_demand_rider !== undefined
                  "
                >
                  <v-chip
                    color="orange"
                    text-color="#fff"
                    v-if="item.fulfillment_type == 'delivery'"
                    >طريقة الاستلام : توصيل</v-chip
                  >
                  <v-chip
                    color="#783D73"
                    text-color="#fff"
                    v-if="item.fulfillment_type == 'takeaway'"
                    >طريقة الاستلام : استلام من المتجر</v-chip
                  >
                  <v-chip
                    color="#317e7f"
                    text-color="#fff"
                    v-if="item.fulfillment_type == 'dine_in'"
                    >طريقة الاستلام : في المتجر</v-chip
                  >
                  <br />
                  <v-chip
                    color="blue"
                    text-color="#fff"
                    v-if="item.is_on_demand_rider"
                    class="mt-2"
                    >بحث عن سائق</v-chip
                  >
                  <v-chip
                    color="green"
                    text-color="#fff"
                    v-if="!item.is_on_demand_rider && item.platform"
                    class="mt-2"
                    >الطلب :
                    {{ item.platform == "mobile" ? "تطبيق" : "موقع الكتروني " }}
                  </v-chip>
                </div>
                <span v-else class="text-muted">—</span>
              </template>

              <!-- Total Price -->
              <template v-slot:[`item.total_price`]="{ item }">
                <span class="stat-count-chip">{{
                  item.total_price !== undefined ? item.total_price : "—"
                }}</span>
              </template>

              <!-- Action Link (Protected by Order Category Permission) -->
              <template v-slot:[`item.actions`]="{ item }">
                <div class="table-actions-group">
                  <router-link
                    v-if="getOrderRoute(item)"
                    class="action-btn view-btn"
                    :to="getOrderRoute(item)"
                    title="عرض التفاصيل"
                  >
                    <i class="fal fa-eye"></i>
                  </router-link>
                  <span
                    v-else
                    class="action-btn-disabled"
                    title="لا تملك صلاحية عرض هذا الطلب"
                  >
                    <i class="fal fa-lock"></i>
                  </span>
                </div>
              </template>

              <!-- Empty State for Orders -->
              <template v-slot:no-data>
                <div class="empty-table-state">
                  <i class="fal fa-shopping-bag-slash empty-icon"></i>
                  <span>{{
                    activeOrderTab === "delivery"
                      ? "لا توجد طلبات توصيل حديثة حالياً"
                      : "لا توجد طلبات طرود حديثة حالياً"
                  }}</span>
                </div>
              </template>
            </v-data-table>
          </main>
        </div>
      </div>

      <!-- ==================== TOP TABLES WITH TABS ==================== -->
      <div
        class="top-tables-tabbed-section mb-6"
        v-if="
          (canStores && (loading || top_stores.length)) ||
          (canClients && (loading || top_clients.length)) ||
          (canDrivers && (loading || top_drivers.length))
        "
      >
        <div class="creative-table-card">
          <!-- Tab Header -->
          <div class="top-tabs-header">
            <div class="top-tabs-nav">
              <button
                v-if="canStores && (loading || top_stores.length)"
                class="top-tab-btn"
                :class="{ active: activeTopTab === 'stores' }"
                @click="activeTopTab = 'stores'"
              >
                <div class="top-tab-icon store-tab-icon">
                  <i class="fal fa-store-alt"></i>
                </div>
                <div class="top-tab-text">
                  <span class="top-tab-label">أفضل المتاجر</span>
                  <span class="top-tab-count" v-if="top_stores.length">{{
                    top_stores.length
                  }}</span>
                </div>
              </button>
              <button
                v-if="canClients && (loading || top_clients.length)"
                class="top-tab-btn"
                :class="{ active: activeTopTab === 'clients' }"
                @click="activeTopTab = 'clients'"
              >
                <div class="top-tab-icon client-tab-icon">
                  <i class="fal fa-users"></i>
                </div>
                <div class="top-tab-text">
                  <span class="top-tab-label">أفضل العملاء</span>
                  <span class="top-tab-count" v-if="top_clients.length">{{
                    top_clients.length
                  }}</span>
                </div>
              </button>
              <button
                v-if="canDrivers && (loading || top_drivers.length)"
                class="top-tab-btn"
                :class="{ active: activeTopTab === 'drivers' }"
                @click="activeTopTab = 'drivers'"
              >
                <div class="top-tab-icon driver-tab-icon">
                  <i class="fal fa-motorcycle"></i>
                </div>
                <div class="top-tab-text">
                  <span class="top-tab-label">أفضل السائقين</span>
                  <span class="top-tab-count" v-if="top_drivers.length">{{
                    top_drivers.length
                  }}</span>
                </div>
              </button>
            </div>
          </div>

          <!-- Tab Content: Stores -->
          <div v-show="activeTopTab === 'stores'" class="top-tab-content">
            <main class="table-body-wrap">
              <v-data-table
                class="thumb strip creative-data-table"
                :headers="store_headers"
                :items="top_stores"
                :loading="loading"
                :loading-text="$t('table.loadingData')"
                item-key="id"
                hide-default-footer
              >
                <template v-slot:[`item.index`]="{ index }">
                  <span class="index-badge">#{{ index + 1 }}</span>
                </template>

                <template v-slot:[`item.store`]="{ item }">
                  <div class="store-cell-item">
                    <div class="store-img-wrapper">
                      <img
                        @click="show_model_1"
                        class="image store-avatar"
                        :src="getStoreImage(item)"
                        @error="
                          $event.target.src = require('@/assets/images/icons/logo.png')
                        "
                      />
                    </div>
                    <div class="store-meta">
                      <span class="store-name">{{ getStoreName(item) }}</span>
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.phone`]="{ item }">
                  <span class="direction-ltr d-inline-block">
                    {{
                      item.phone_complete_form ||
                      item.phone ||
                      item.store?.phone ||
                      "—"
                    }}
                  </span>
                </template>

                <template v-slot:[`item.orders_count`]="{ item }">
                  <span class="stat-count-chip">
                    {{
                      item.orders_count !== undefined
                        ? item.orders_count
                        : item.count !== undefined
                        ? item.count
                        : "—"
                    }}
                  </span>
                </template>

                <template v-slot:[`item.is_active`]="{ item }">
                  <span
                    class="creative-status-chip"
                    :class="
                      item.is_active || item.store?.is_active
                        ? 'status-active'
                        : 'status-inactive'
                    "
                  >
                    <span class="dot-indicator"></span>
                    {{
                      item.is_active || item.store?.is_active
                        ? "فعال"
                        : "غير فعال"
                    }}
                  </span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="table-actions-group">
                    <router-link
                      v-if="canStores"
                      class="action-btn view-btn"
                      :to="`/store/show/${item.id || item.store?.id}`"
                      title="عرض التفاصيل"
                    >
                      <i class="fal fa-eye"></i>
                    </router-link>
                  </div>
                </template>

                <template v-slot:no-data>
                  <div class="empty-table-state">
                    <i class="fal fa-store-slash empty-icon"></i>
                    <span>{{ $t("table.noData") }}</span>
                  </div>
                </template>
              </v-data-table>
            </main>
          </div>

          <!-- Tab Content: Clients -->
          <div v-show="activeTopTab === 'clients'" class="top-tab-content">
            <main class="table-body-wrap">
              <v-data-table
                class="thumb strip creative-data-table"
                :headers="client_headers"
                :items="top_clients"
                :loading="loading"
                :loading-text="$t('table.loadingData')"
                item-key="id"
                hide-default-footer
              >
                <template v-slot:[`item.index`]="{ index }">
                  <span class="index-badge">#{{ index + 1 }}</span>
                </template>

                <template v-slot:[`item.client`]="{ item }">
                  <div class="store-cell-item">
                    <div class="store-img-wrapper">
                      <img
                        @click="show_model_1"
                        class="image store-avatar"
                        :src="
                          item.avatar ||
                          require('@/assets/images/Avatars/no-user.png')
                        "
                        @error="
                          $event.target.src = require('@/assets/images/icons/logo.png')
                        "
                      />
                    </div>
                    <div class="store-meta">
                      <span class="store-name">{{
                        item.full_name || item.name || "عميل"
                      }}</span>
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.phone`]="{ item }">
                  <span class="direction-ltr d-inline-block">
                    {{ item.phone_complete_form || item.phone || "—" }}
                  </span>
                </template>

                <template v-slot:[`item.email`]="{ item }">
                  <span>{{ item.email || "—" }}</span>
                </template>

                <template v-slot:[`item.orders_count`]="{ item }">
                  <span class="stat-count-chip">
                    {{
                      item.orders_count !== undefined
                        ? item.orders_count
                        : item.count !== undefined
                        ? item.count
                        : 0
                    }}
                  </span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="table-actions-group">
                    <router-link
                      v-if="canClients"
                      class="action-btn view-btn"
                      :to="`/client/show/${item.id}`"
                      title="عرض التفاصيل"
                    >
                      <i class="fal fa-eye"></i>
                    </router-link>
                  </div>
                </template>

                <template v-slot:no-data>
                  <div class="empty-table-state">
                    <i class="fal fa-user-slash empty-icon"></i>
                    <span>{{ $t("table.noData") }}</span>
                  </div>
                </template>
              </v-data-table>
            </main>
          </div>

          <!-- Tab Content: Drivers -->
          <div v-show="activeTopTab === 'drivers'" class="top-tab-content">
            <main class="table-body-wrap">
              <v-data-table
                class="thumb strip creative-data-table"
                :headers="driver_headers"
                :items="top_drivers"
                :loading="loading"
                :loading-text="$t('table.loadingData')"
                item-key="id"
                hide-default-footer
              >
                <template v-slot:[`item.index`]="{ index }">
                  <span class="index-badge">#{{ index + 1 }}</span>
                </template>

                <template v-slot:[`item.driver`]="{ item }">
                  <div class="store-cell-item">
                    <div class="store-img-wrapper">
                      <img
                        @click="show_model_1"
                        class="image store-avatar"
                        :src="
                          item.avatar ||
                          require('@/assets/images/Avatars/no-user.png')
                        "
                        @error="
                          $event.target.src = require('@/assets/images/icons/logo.png')
                        "
                      />
                    </div>
                    <div class="store-meta">
                      <span class="store-name">{{
                        item.full_name || item.name || "سائق"
                      }}</span>
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.phone`]="{ item }">
                  <span class="direction-ltr d-inline-block">
                    {{ item.phone_complete_form || item.phone || "—" }}
                  </span>
                </template>

                <template v-slot:[`item.orders_count`]="{ item }">
                  <span class="stat-count-chip">
                    {{
                      item.completed_orders_count !== undefined
                        ? item.completed_orders_count
                        : item.orders_count !== undefined
                        ? item.orders_count
                        : item.count !== undefined
                        ? item.count
                        : 0
                    }}
                  </span>
                </template>

                <template v-slot:[`item.rate`]="{ item }">
                  <div class="d-inline-flex align-items-center rating-pill">
                    <i class="fas fa-star text-warning ml-1"></i>
                    <span>{{ item.rate }}</span>
                  </div>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="table-actions-group">
                    <router-link
                      v-if="canDrivers"
                      class="action-btn view-btn"
                      :to="`/driver/show/${item.id}`"
                      title="عرض التفاصيل"
                    >
                      <i class="fal fa-eye"></i>
                    </router-link>
                  </div>
                </template>

                <template v-slot:no-data>
                  <div class="empty-table-state">
                    <i class="fal fa-id-card empty-icon"></i>
                    <span>{{ $t("table.noData") }}</span>
                  </div>
                </template>
              </v-data-table>
            </main>
          </div>
        </div>
      </div>

      <!-- ==================== DASHBOARD OVERALL EMPTY STATE ==================== -->
      <div
        v-if="!loading && isDashboardCompletelyEmpty"
        class="empty-dashboard-container py-12 text-center"
      >
        <div class="empty-dashboard-box">
          <i class="fal fa-chart-network empty-dash-icon"></i>
          <h3>لا توجد بيانات إحصائية متاحة حالياً</h3>
          <p class="text-muted">
            لم يتم العثور على أية بيانات في نطاق الفلترة المحدد أو أن الصلاحيات
            المتاحة لا تشمل بيانات لعرضها.
          </p>
          <button class="hero-btn primary-btn mt-4" @click="resetDateFilter">
            <i class="fal fa-sync-alt"></i>
            <span>إعادة تعيين الفلتر</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== QUICK SEARCH MODAL ==================== -->
    <v-dialog v-model="search_model" max-width="850">
      <v-card class="search-modal-card">
        <div class="modal-header-strip">
          <h3>
            <i class="fal fa-search ml-2"></i>
            مركز البحث السريع
          </h3>
          <v-btn icon @click="search_model = false" class="close-modal-btn">
            <v-icon>fal fa-times</v-icon>
          </v-btn>
        </div>

        <div class="px-6 pt-4">
          <div class="search-type-selector mb-4">
            <label class="selector-label">اختر نوع البحث:</label>
            <v-radio-group v-model="search_type" row hide-details class="mt-0">
              <v-radio label="طلب" value="order" color="#783d73"></v-radio>
              <v-radio label="مستخدم" value="user" color="#783d73"></v-radio>
            </v-radio-group>
          </div>

          <div class="search-input-wrap mb-4">
            <i class="fal fa-search search-input-icon"></i>
            <input
              v-model="search_term"
              @input="search"
              class="search_home_input_creative"
              type="number"
              :placeholder="
                search_type == 'order'
                  ? 'أدخل رقم الطلب...'
                  : 'أدخل رقم الهاتف...'
              "
            />
          </div>
        </div>

        <v-divider class="my-0" />

        <v-card-text class="pt-4">
          <div
            v-if="!search_result.length"
            class="text-center py-6 empty-search"
          >
            <i class="fal fa-search-minus search-empty-icon mb-2"></i>
            <p>لا توجد نتائج للبحث حالياً</p>
          </div>
          <div v-else>
            <v-data-table
              class="thumb strip creative-data-table"
              :headers="search_type == 'user' ? headers : headers_orders"
              :items="search_result"
              :custom-filter="helper_filterSearch"
              :loading="loading_table"
              :loading-text="$t('table.loadingData')"
              item-key="id"
              hide-default-footer
            >
              <template v-slot:[`item.avatar`]="{ item }">
                <img
                  @click="show_model_1"
                  class="image store-avatar"
                  :src="
                    search_type == 'user'
                      ? item.avatar
                      : item.client
                      ? item.client.avatar
                      : ''
                  "
                />
              </template>

              <template v-slot:[`item.index`]="{ index }">
                #{{ index + 1 }}
              </template>

              <template v-slot:[`item.is_ban`]="{ item }">
                <v-btn
                  small
                  rounded
                  depressed
                  :color="item.is_ban ? 'error' : 'success'"
                >
                  {{ item.is_ban ? "محظور" : "نشط" }}
                </v-btn>
              </template>

              <template v-slot:[`item.email`]="{ item }">
                <span>{{ item.email || "لا يوجد" }}</span>
              </template>

              <template v-slot:[`item.user_type`]="{ item }">
                <span class="user-type-tag">{{
                  { driver: "سائق", restaurant: "متجر", client: "عميل" }[
                    item.user_type
                  ] || item.user_type
                }}</span>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="table-actions-group">
                  <v-btn
                    icon
                    small
                    class="action-btn view-btn"
                    @click="routeToPage(item)"
                  >
                    <v-icon small>fal fa-eye</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Image Preview Modal -->
    <v-dialog v-model="model_1.show_model" max-width="500">
      <v-card class="pa-2 text-center">
        <img :src="model_1.model_img_src" class="img-fluid rounded" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { USER_TYPES } from "@/helpers/roles.js";

export default {
  name: "HomePage",

  data() {
    return {
      loadingPermissions: false,
      loading: false,
      filter_from: "",
      filter_to: "",
      filter_error: null,

      // Dashboard API Datasets
      statistics_data: [],
      orders_data: null,
      activeOrderTab: "delivery",
      charts_data: {
        driver_gender: [],
        order_gender: [],
      },
      top_stores: [],
      top_clients: [],
      top_drivers: [],
      activeTopTab: "stores",
      pending_actions_data: [],

      // Search Modal
      search_model: false,
      search_type: "order",
      search_term: null,
      search_result: [],
      loading_table: false,

      // Image modal
      model_1: {
        show_model: false,
        model_img_src: "",
      },

      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
      ],

      // Table Headers
      store_headers: [
        { text: "#", align: "center", value: "index", sortable: true },
        { text: "المتجر", align: "center", value: "store", sortable: false },
        { text: "رقم الهاتف", value: "phone", align: "center" },
        { text: "عدد الطلبات", value: "orders_count", align: "center" },
        // { text: "الحالة", value: "is_active", align: "center" },
        { text: "التحكم", value: "actions", align: "center", sortable: false },
      ],
      delivery_order_headers: [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: true,
          width: "80",
        },
        {
          text: "رقم الطلب",
          align: "center",
          value: "order_number",
          sortable: false,
        },
        { text: "العميل", align: "center", value: "client", sortable: false },
        { text: "السائق", align: "center", value: "driver", sortable: false },
        {
          text: "المتجر",
          align: "center",
          value: "delivery_type",
          sortable: false,
        },
        {
          text: "الحالة",
          align: "center",
          value: "status",
          sortable: false,
          width: "200",
        },
        {
          text: "تاريخ الإنشاء",
          align: "center",
          value: "created_at",
          sortable: false,
        },
        {
          text: "طريقة الدفع",
          align: "center",
          value: "payment_type",
          sortable: false,
        },
        {
          text: "تفاصيل اضافية",
          align: "center",
          value: "notes",
          sortable: false,
        },
        {
          text: "السعر الإجمالي",
          align: "center",
          value: "total_price",
          sortable: false,
        },
        { text: "التحكم", value: "actions", sortable: false, align: "center" },
      ],
      package_order_headers: [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: true,
          width: "80",
        },
        {
          text: "رقم الطلب",
          align: "center",
          value: "order_number",
          sortable: false,
        },
        { text: "العميل", align: "center", value: "client", sortable: false },
        {
          text: "هاتف العميل",
          align: "center",
          value: "client_phone",
          sortable: false,
        },
        { text: "السائق", align: "center", value: "driver", sortable: false },
        {
          text: "الحالة",
          align: "center",
          value: "status",
          sortable: false,
          width: "200",
        },
        { text: "المنصة", align: "center", value: "platform", sortable: false },
        {
          text: "تاريخ الإنشاء",
          align: "center",
          value: "created_at",
          sortable: false,
        },
        {
          text: "طريقة الدفع",
          align: "center",
          value: "payment_type",
          sortable: false,
        },
        {
          text: "تفاصيل اضافية",
          align: "center",
          value: "notes",
          sortable: false,
        },
        {
          text: "السعر الإجمالي",
          align: "center",
          value: "total_price",
          sortable: false,
        },
        { text: "التحكم", value: "actions", sortable: false, align: "center" },
      ],
      order_headers: [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: true,
          width: "80",
        },
        {
          text: "رقم الطلب",
          align: "center",
          value: "order_number",
          sortable: false,
        },
        { text: "العميل", align: "center", value: "client", sortable: false },
        { text: "السائق", align: "center", value: "driver", sortable: false },
        {
          text: "المتجر",
          align: "center",
          value: "delivery_type",
          sortable: false,
        },
        {
          text: "الحالة",
          align: "center",
          value: "status",
          sortable: false,
          width: "200",
        },
        {
          text: "تاريخ الإنشاء",
          align: "center",
          value: "created_at",
          sortable: false,
        },
        {
          text: "طريقة الدفع",
          align: "center",
          value: "payment_type",
          sortable: false,
        },
        {
          text: "تفاصيل اضافية",
          align: "center",
          value: "notes",
          sortable: false,
        },
        {
          text: "السعر الإجمالي",
          align: "center",
          value: "total_price",
          sortable: false,
        },
        { text: "التحكم", value: "actions", sortable: false, align: "center" },
      ],
      client_headers: [
        { text: "#", align: "center", value: "index", sortable: true },
        { text: "العميل", align: "center", value: "client", sortable: false },
        { text: "رقم الهاتف", value: "phone", align: "center" },
        { text: "البريد الإلكتروني", value: "email", align: "center" },
        { text: "عدد الطلبات", value: "orders_count", align: "center" },
        { text: "التحكم", value: "actions", align: "center", sortable: false },
      ],
      driver_headers: [
        { text: "#", align: "center", value: "index", sortable: true },
        { text: "السائق", align: "center", value: "driver", sortable: false },
        { text: "رقم الهاتف", value: "phone", align: "center" },
        { text: "الطلبات المكتملة", value: "orders_count", align: "center" },
        { text: "التقييم", value: "rate", align: "center" },
        { text: "التحكم", value: "actions", align: "center", sortable: false },
      ],
    };
  },

  computed: {
    // ==================== AUTH & PERMISSIONS ====================
    currentUser() {
      return this.$store?.getters["auth_module/currentUser"] || {};
    },

    // superadmin & support bypass the permissions list
    isSuperAdmin() {
      const userType = (
        this.currentUser?.usertype ||
        localStorage.getItem("zoz_dash_User_Type") ||
        ""
      )
        .toString()
        .trim()
        .toLowerCase();
      return (
        userType === USER_TYPES.SUPER_ADMIN || userType === USER_TYPES.SUPPORT
      );
    },

    permissions() {
      const storePerms = this.$store?.getters["permissions"];
      if (Array.isArray(storePerms) && storePerms.length) return storePerms;
      try {
        const stored = localStorage.getItem("Zoz_permissions");
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        return [];
      }
    },

    hasStatisticsPermission() {
      return this.can("statistics.index");
    },

    canDeliveryOrders() {
      return this.can("delivery_order.index");
    },

    canPackageOrders() {
      return this.can("package_order.index");
    },

    canOrders() {
      return this.canDeliveryOrders || this.canPackageOrders;
    },

    canStores() {
      return this.can("store.index") || this.canFront("store/show-all");
    },

    canClients() {
      return this.can("client.index") || this.canFront("client/show-all");
    },

    canDrivers() {
      return this.can("driver.index") || this.canFront("driver/show-all");
    },

    canAdmins() {
      return this.can("admin.index") || this.canFront("admin/show-all");
    },

    canCompanies() {
      return this.can("company.index") || this.canFront("company/show-all");
    },

    canCashoutRequests() {
      return this.can("cashout_requests.index");
    },

    canUpdateProfile() {
      return this.can("update_profile.index");
    },

    canUserRewards() {
      return this.can("user_rewards.index");
    },

    canDriverGenderChart() {
      return this.canDrivers;
    },

    canOrderGenderChart() {
      return this.canOrders;
    },

    canAnyGenderChart() {
      return this.canDriverGenderChart || this.canOrderGenderChart;
    },

    selectedCountryId() {
      try {
        const country = JSON.parse(localStorage.getItem("zoz_dash_country"));
        return country?.id !== undefined ? country.id : -1;
      } catch (e) {
        return -1;
      }
    },

    // ==================== FILTERED DATASETS ====================
    visibleStatCards() {
      return (this.statistics_data || []).filter((item) => this.canCard(item));
    },

    visiblePendingActions() {
      return (this.pending_actions_data || []).filter((item) =>
        this.canAction(item)
      );
    },

    driverGenderData() {
      const list = this.charts_data?.driver_gender;
      if (Array.isArray(list) && list.length) return list;
      return [
        { key: "male", count: 0 },
        { key: "female", count: 0 },
        { key: "unknown", count: 0 },
      ];
    },

    orderGenderData() {
      const list = this.charts_data?.order_gender;
      if (Array.isArray(list) && list.length) return list;
      return [
        { key: "male", count: 0 },
        { key: "female", count: 0 },
        { key: "unknown", count: 0 },
      ];
    },

    hasDriverGenderData() {
      return this.getGenderTotal(this.driverGenderData) > 0;
    },

    hasOrderGenderData() {
      return this.getGenderTotal(this.orderGenderData) > 0;
    },

    hasAnyGenderData() {
      return this.hasDriverGenderData || this.hasOrderGenderData;
    },

    // ==================== ORDERS TABS DATA ====================
    delivery_orders() {
      if (!this.orders_data) return [];
      if (Array.isArray(this.orders_data)) {
        const filtered = this.orders_data.filter(
          (o) => (o.order_type || o.type) === "delivery"
        );
        return filtered.length ? filtered : this.orders_data;
      }
      return Array.isArray(this.orders_data.delivery)
        ? this.orders_data.delivery
        : [];
    },

    package_orders() {
      if (!this.orders_data) return [];
      if (Array.isArray(this.orders_data)) {
        return this.orders_data.filter(
          (o) => (o.order_type || o.type) === "package"
        );
      }
      return Array.isArray(this.orders_data.package)
        ? this.orders_data.package
        : [];
    },

    currentOrderItems() {
      return this.activeOrderTab === "package"
        ? this.package_orders
        : this.delivery_orders;
    },

    currentOrderHeaders() {
      return this.activeOrderTab === "package"
        ? this.package_order_headers
        : this.delivery_order_headers;
    },

    totalOrdersCount() {
      return this.delivery_orders.length + this.package_orders.length;
    },

    isDashboardCompletelyEmpty() {
      const hasCards = this.visibleStatCards.length > 0;
      const hasActions = this.visiblePendingActions.length > 0;
      const hasOrders = this.totalOrdersCount > 0;
      const hasStores = this.top_stores && this.top_stores.length > 0;
      const hasClients = this.top_clients && this.top_clients.length > 0;
      const hasDrivers = this.top_drivers && this.top_drivers.length > 0;
      const hasCharts = this.hasAnyGenderData;
      return (
        !hasCards &&
        !hasActions &&
        !hasOrders &&
        !hasStores &&
        !hasClients &&
        !hasDrivers &&
        !hasCharts
      );
    },

    // ==================== QUICK LINKS FOR RESTRICTED ADMIN ====================
    allowedQuickLinks() {
      const links = [];
      if (this.canClients) {
        links.push({
          title: "العملاء",
          path: "/client/show-all",
          icon: "fal fa-users",
        });
      }
      if (this.canDrivers) {
        links.push({
          title: "السائقين",
          path: "/driver/show-all",
          icon: "fal fa-motorcycle",
        });
      }
      if (this.canStores) {
        links.push({
          title: "المتاجر",
          path: "/store/show-all",
          icon: "fal fa-store",
        });
      }
      if (this.canDeliveryOrders) {
        links.push({
          title: "طلبات التوصيل",
          path: "/delivery_orders/show-all",
          icon: "fal fa-shipping-fast",
        });
      }
      if (this.canPackageOrders) {
        links.push({
          title: "طلبات الطرود",
          path: "/package_order/show-all",
          icon: "fal fa-box",
        });
      }
      if (this.canAdmins) {
        links.push({
          title: "المسؤولين",
          path: "/admin/show-all",
          icon: "fal fa-user-shield",
        });
      }
      if (this.canCompanies) {
        links.push({
          title: "الشركات",
          path: "/company/show-all",
          icon: "fal fa-building",
        });
      }
      return links;
    },

    // ==================== APEXCHARTS CONFIG ====================
    driverDonutSeries() {
      return this.getGenderSeries(this.driverGenderData);
    },

    driverDonutOptions() {
      return this.createDonutChartConfig("السائقين", [
        "ذكور",
        "إناث",
        "غير محدد",
      ]);
    },

    orderDonutSeries() {
      return this.getGenderSeries(this.orderGenderData);
    },

    orderDonutOptions() {
      return this.createDonutChartConfig("الطلبات", [
        "ذكور",
        "إناث",
        "غير محدد",
      ]);
    },

    // Search dialog headers
    headers() {
      return [
        { text: "#", align: "center", value: "index", sortable: true },
        { text: "الصورة", align: "center", value: "avatar", sortable: false },
        { text: "الاسم", value: "full_name", align: "center" },
        { text: "رقم الهاتف", value: "phone", align: "center" },
        { text: "البريد الالكتروني", value: "email", align: "center" },
        { text: "نوع المستخدم", value: "user_type", align: "center" },
        { text: "التحكم", value: "actions", align: "center", sortable: false },
      ];
    },
    headers_orders() {
      return [
        { text: "#", align: "center", value: "index", sortable: true },
        {
          text: "رقم الطلب",
          align: "center",
          value: "order_number",
          sortable: true,
        },
        { text: "الصورة", align: "center", value: "avatar", sortable: false },
        { text: "الاسم", value: "client.full_name", align: "center" },
        {
          text: "رقم الهاتف",
          value: "client.phone_complete_form",
          align: "center",
        },
        { text: "التحكم", value: "actions", align: "center", sortable: false },
      ];
    },
  },

  methods: {
    // ==================== PERMISSION HELPERS ====================
    can(backRouteName) {
      if (this.isSuperAdmin) return true;
      if (!backRouteName) return false;
      return this.permissions.some(
        (p) => (p.back_route_name || "").trim() === backRouteName.trim()
      );
    },

    canFront(frontRouteName) {
      if (this.isSuperAdmin) return true;
      if (!frontRouteName) return false;
      const clean = frontRouteName.replace(/^\/+/, "").trim();
      return this.permissions.some((p) => {
        const pFront = (p.front_route_name || "").replace(/^\/+/, "").trim();
        const pUrl = (p.url || "").replace(/^\/+/, "").trim();
        return pFront === clean || pUrl === clean;
      });
    },

    canCard(item) {
      if (this.isSuperAdmin) return true;
      if (!item) return false;

      const route = (item.route_front || "").replace(/^\/+/, "").trim();
      if (route && this.canFront(route)) return true;

      const title = (item.title || "").toLowerCase();
      if (
        title.includes("client") ||
        title.includes("عملاء") ||
        title.includes("عميل") ||
        route.includes("client")
      ) {
        return this.canClients;
      }
      if (
        title.includes("driver") ||
        title.includes("سائق") ||
        route.includes("driver")
      ) {
        return this.canDrivers;
      }
      if (
        title.includes("store") ||
        title.includes("restaurant") ||
        title.includes("متجر") ||
        title.includes("متاجر") ||
        route.includes("store")
      ) {
        return this.canStores;
      }
      if (
        title.includes("admin") ||
        title.includes("أدمن") ||
        title.includes("مسؤول") ||
        route.includes("admin")
      ) {
        return this.canAdmins;
      }
      if (
        title.includes("compan") ||
        title.includes("شرك") ||
        route.includes("company")
      ) {
        return this.canCompanies;
      }
      if (
        title.includes("delivery") ||
        title.includes("توصيل") ||
        route.includes("delivery")
      ) {
        return this.canDeliveryOrders;
      }
      if (
        title.includes("package") ||
        title.includes("طرد") ||
        title.includes("باكدج") ||
        route.includes("package")
      ) {
        return this.canPackageOrders;
      }
      if (
        title.includes("cashout") ||
        title.includes("سحب") ||
        route.includes("transaction")
      ) {
        return this.canCashoutRequests;
      }
      if (
        title.includes("update_profile") ||
        title.includes("تحديث") ||
        route.includes("update_profile")
      ) {
        return this.canUpdateProfile;
      }
      if (
        title.includes("reward") ||
        title.includes("مكافآت") ||
        route.includes("user_rewards")
      ) {
        return this.canUserRewards;
      }

      return route ? this.canFront(route) : false;
    },

    canAction(item) {
      if (this.isSuperAdmin) return true;
      if (!item) return false;
      if (
        item.key === "pending_cashout_requests" ||
        item.key?.includes("cashout")
      ) {
        return this.canCashoutRequests;
      }
      if (item.key?.includes("update_profile")) {
        return this.canUpdateProfile;
      }
      if (item.key?.includes("user_rewards")) {
        return this.canUserRewards;
      }
      if (item.key?.includes("delivery_order")) {
        return this.canDeliveryOrders;
      }
      if (item.key?.includes("package_order")) {
        return this.canPackageOrders;
      }
      return this.canCard(item);
    },

    canOpenRoute(routeFront) {
      if (!routeFront || routeFront === "#") return false;
      if (this.isSuperAdmin) return true;
      return this.canFront(routeFront);
    },

    canOrder(order) {
      if (this.isSuperAdmin) return true;
      if (!order) return false;
      const orderType = order.type || order.order_type;
      if (orderType === "delivery") return this.canDeliveryOrders;
      if (orderType === "package") return this.canPackageOrders;
      return false;
    },

    getOrderRoute(order) {
      if (!this.canOrder(order)) return null;
      const orderType = order.type || order.order_type;
      if (orderType === "delivery") {
        return `/delivery_orders/show/${order.id}`;
      }
      if (orderType === "package") {
        return `/package_order/show/${order.id}`;
      }
      return null;
    },

    getOrderType(order) {
      return order.type || order.order_type || "delivery";
    },

    formatOrderCategory(type) {
      if (type === "package") return "طلب طرد";
      if (type === "delivery") return "طلب توصيل";
      return type || "طلب";
    },

    getDetailPillClass(key) {
      const classMap = {
        fulfillment_type: "pill-fulfillment",
        platform: "pill-platform",
        is_on_demand_rider: "pill-ondemand",
      };
      return classMap[key] || "pill-default";
    },

    getDetailPillIcon(key) {
      const iconMap = {
        fulfillment_type: "fal fa-shipping-fast",
        platform: "fal fa-globe",
        is_on_demand_rider: "fal fa-search",
      };
      return iconMap[key] || "fal fa-info-circle";
    },

    getDetailChipColor(key) {
      const colorMap = {
        fulfillment_type: "orange",
        platform: "green",
        is_on_demand_rider: "blue",
      };
      return colorMap[key] || "#64748b";
    },

    // ==================== DATA FETCHING ====================
    async initDashboard() {
      this.loadingPermissions = true;
      // If permissions not yet loaded and user is not superadmin, load them first
      if (
        !this.isSuperAdmin &&
        !this.isSupport &&
        (!this.permissions || !this.permissions.length)
      ) {
        try {
          const res = await this.$axios.get("my_permissions");
          const data = res.data?.data || [];
          localStorage.setItem("Zoz_permissions", JSON.stringify(data));
          this.$store?.commit("setPermissions", data);
        } catch (err) {
          if (err.response?.status === 422) {
            localStorage.setItem("Zoz_permissions", JSON.stringify([]));
            this.$store?.commit("setPermissions", []);
          }
        }
      }
      this.loadingPermissions = false;

      // Check statistics.index permission before fetching data
      if (this.hasStatisticsPermission) {
        this.getStatisticsData();
      }
    },

    getStatisticsData() {
      // Validate dates
      if (this.filter_from && this.filter_to) {
        if (new Date(this.filter_to) < new Date(this.filter_from)) {
          this.filter_error =
            "تاريخ النهاية يجب أن يكون بعد أو يساوي تاريخ البداية";
          return;
        }
      }
      this.filter_error = null;
      this.loading = true;

      const params = {
        time_zone_country_id: this.selectedCountryId,
      };
      if (this.filter_from) params.from = this.filter_from;
      if (this.filter_to) params.to = this.filter_to;

      this.$axios({
        method: "GET",
        url: "statistics",
        params: params,
      })
        .then((res) => {
          const data = res.data?.data || {};
          this.statistics_data = Array.isArray(data.statistics)
            ? data.statistics
            : [];
          this.orders_data = data.orders !== undefined ? data.orders : null;
          this.charts_data = data.charts || {
            driver_gender: [],
            order_gender: [],
          };
          this.top_stores = Array.isArray(data.top_stores)
            ? data.top_stores
            : Array.isArray(data.popular)
            ? data.popular
            : [];
          this.top_clients = Array.isArray(data.top_clients)
            ? data.top_clients
            : [];
          this.top_drivers = Array.isArray(data.top_drivers)
            ? data.top_drivers
            : [];
          this.pending_actions_data = Array.isArray(data.pending_actions)
            ? data.pending_actions
            : [];
          this.loading = false;
          if (
            !this.delivery_orders.length &&
            this.package_orders.length &&
            this.canPackageOrders
          ) {
            this.activeOrderTab = "package";
          }
        })
        .catch((err) => {
          this.loading = false;
          if (err.response?.status === 401) {
            this.$store?.dispatch("auth_module/logOut");
            this.$router.push("/login").catch(() => {});
            return;
          }
          if (err.response?.status === 422) {
            const message =
              err.response?.data?.message ||
              err.response?.data?.messages ||
              "خطأ في التحقق من صحة الفلتر";
            this.filter_error =
              typeof message === "string" ? message : JSON.stringify(message);
            if (this.$iziToast) {
              this.$iziToast.error({
                title: this.$t("validation.error"),
                message: this.filter_error,
              });
            }
            return;
          }
          const message = err.response?.data?.message || err.message;
          if (this.$iziToast) {
            this.$iziToast.error({
              title: this.$t("validation.error"),
              message: message,
            });
          }
        });
    },

    applyDateFilter() {
      this.updateQueryParams();
      this.getStatisticsData();
    },

    resetDateFilter() {
      this.filter_from = "";
      this.filter_to = "";
      this.filter_error = null;
      this.updateQueryParams();
      this.getStatisticsData();
    },

    updateQueryParams() {
      const url = new URL(window.location);
      if (this.filter_from) {
        url.searchParams.set("from", this.filter_from);
      } else {
        url.searchParams.delete("from");
      }
      if (this.filter_to) {
        url.searchParams.set("to", this.filter_to);
      } else {
        url.searchParams.delete("to");
      }
      window.history.replaceState({}, "", url);
    },

    // ==================== APEXCHARTS CONFIG BUILDER ====================
    getGenderSeries(genderList) {
      if (!Array.isArray(genderList) || !genderList.length) return [0, 0, 0];
      const male = Number(genderList.find((g) => g.key === "male")?.count) || 0;
      const female =
        Number(genderList.find((g) => g.key === "female")?.count) || 0;
      const unknown =
        Number(genderList.find((g) => g.key === "unknown")?.count) || 0;
      return [male, female, unknown];
    },

    getGenderTotal(genderList) {
      if (!Array.isArray(genderList)) return 0;
      return genderList.reduce(
        (acc, curr) => acc + (Number(curr.count) || 0),
        0
      );
    },

    createDonutChartConfig(categoryName, labels) {
      return {
        chart: {
          type: "donut",
          fontFamily: "inherit",
          animations: {
            enabled: true,
            speed: 600,
          },
        },
        labels: labels,
        colors: ["#3b82f6", "#ec4899", "#94a3b8"],
        dataLabels: {
          enabled: false,
        },
        plotOptions: {
          pie: {
            customScale: 0.95,
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#64748b",
                  offsetY: -6,
                },
                value: {
                  show: true,
                  fontSize: "22px",
                  fontWeight: 800,
                  color: "#1e293b",
                  offsetY: 6,
                  formatter: (val) => val,
                },
                total: {
                  show: true,
                  label: "الإجمالي",
                  fontSize: "13px",
                  color: "#94a3b8",
                  formatter: (w) => {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  },
                },
              },
            },
          },
        },
        legend: {
          position: "bottom",
          horizontalAlign: "center",
          fontSize: "13px",
          fontWeight: 600,
          markers: {
            radius: 6,
          },
          itemMargin: {
            horizontal: 10,
            vertical: 4,
          },
        },
        stroke: {
          width: 3,
          colors: ["#ffffff"],
        },
        tooltip: {
          y: {
            formatter: (val) => `${val} (${categoryName})`,
          },
        },
      };
    },

    formatGenderLabel(key) {
      if (key === "male") return "ذكور";
      if (key === "female") return "إناث";
      return "غير محدد";
    },

    getGenderIcon(key) {
      if (key === "male") return "fal fa-mars";
      if (key === "female") return "fal fa-venus";
      return "fal fa-genderless";
    },

    // ==================== UI STYLING & THEMING ====================
    getCardTheme(index) {
      const themes = [
        {
          color: "#783d73",
          secondary: "#a05a99",
          bgGradient:
            "linear-gradient(135deg, rgba(120, 61, 115, 0.09) 0%, rgba(160, 90, 153, 0.02) 100%)",
          glow: "rgba(120, 61, 115, 0.22)",
          iconBg: "rgba(120, 61, 115, 0.12)",
          defaultIcon: "fal fa-users",
        },
        {
          color: "#10b981",
          secondary: "#059669",
          bgGradient:
            "linear-gradient(135deg, rgba(16, 185, 129, 0.09) 0%, rgba(5, 150, 105, 0.02) 100%)",
          glow: "rgba(16, 185, 129, 0.22)",
          iconBg: "rgba(16, 185, 129, 0.12)",
          defaultIcon: "fal fa-store",
        },
        {
          color: "#f59e0b",
          secondary: "#d97706",
          bgGradient:
            "linear-gradient(135deg, rgba(245, 158, 11, 0.09) 0%, rgba(217, 119, 6, 0.02) 100%)",
          glow: "rgba(245, 158, 11, 0.22)",
          iconBg: "rgba(245, 158, 11, 0.12)",
          defaultIcon: "fal fa-shopping-bag",
        },
        {
          color: "#ec4899",
          secondary: "#d946ef",
          bgGradient:
            "linear-gradient(135deg, rgba(236, 72, 153, 0.09) 0%, rgba(217, 70, 239, 0.02) 100%)",
          glow: "rgba(236, 72, 153, 0.22)",
          iconBg: "rgba(236, 72, 153, 0.12)",
          defaultIcon: "fal fa-motorcycle",
        },
        {
          color: "#06b6d4",
          secondary: "#0284c7",
          bgGradient:
            "linear-gradient(135deg, rgba(6, 182, 212, 0.09) 0%, rgba(2, 132, 199, 0.02) 100%)",
          glow: "rgba(6, 182, 212, 0.22)",
          iconBg: "rgba(6, 182, 212, 0.12)",
          defaultIcon: "fal fa-chart-line",
        },
        {
          color: "#6366f1",
          secondary: "#4f46e5",
          bgGradient:
            "linear-gradient(135deg, rgba(99, 102, 241, 0.09) 0%, rgba(79, 70, 229, 0.02) 100%)",
          glow: "rgba(99, 102, 241, 0.22)",
          iconBg: "rgba(99, 102, 241, 0.12)",
          defaultIcon: "fal fa-boxes",
        },
      ];
      return themes[index % themes.length];
    },

    getCardStyle(index) {
      const theme = this.getCardTheme(index);
      return {
        "--card-theme-color": theme.color,
        "--card-glow-color": theme.glow,
        "--card-bg-gradient": theme.bgGradient,
        "--card-icon-bg": theme.iconBg,
      };
    },

    getItemIcon(item, index) {
      if (!item || !item.title) return this.getCardTheme(index).defaultIcon;
      const title = item.title.toLowerCase();
      if (
        title.includes("عملاء") ||
        title.includes("مستخدم") ||
        title.includes("user") ||
        title.includes("client")
      ) {
        return "fal fa-users";
      }
      if (
        title.includes("متجر") ||
        title.includes("متاجر") ||
        title.includes("store") ||
        title.includes("restaurant")
      ) {
        return "fal fa-store";
      }
      if (
        title.includes("سائق") ||
        title.includes("سائقين") ||
        title.includes("driver")
      ) {
        return "fal fa-motorcycle";
      }
      if (
        title.includes("طلب") ||
        title.includes("طلبات") ||
        title.includes("order")
      ) {
        return "fal fa-shopping-bag";
      }
      if (
        title.includes("ربح") ||
        title.includes("أرباح") ||
        title.includes("مبيعات") ||
        title.includes("revenue") ||
        title.includes("سحب") ||
        title.includes("cashout")
      ) {
        return "fal fa-coins";
      }
      if (
        title.includes("منتج") ||
        title.includes("منتجات") ||
        title.includes("product")
      ) {
        return "fal fa-box-open";
      }
      if (title.includes("admin") || title.includes("مسؤول")) {
        return "fal fa-user-shield";
      }
      if (title.includes("compan") || title.includes("شرك")) {
        return "fal fa-building";
      }
      return this.getCardTheme(index).defaultIcon;
    },

    getItemTrend(item, index) {
      const trends = [
        { type: "up", value: "+14%", icon: "fal fa-arrow-up" },
        { type: "up", value: "+8%", icon: "fal fa-arrow-up" },
        { type: "up", value: "+22%", icon: "fal fa-arrow-up" },
        { type: "neutral", value: "+5%", icon: "fal fa-level-up-alt" },
        { type: "up", value: "+18%", icon: "fal fa-arrow-up" },
      ];
      return trends[index % trends.length];
    },

    getActionIcon(action) {
      const key = (action.key || "").toLowerCase();
      if (key.includes("cashout") || key.includes("withdraw"))
        return "fal fa-money-bill-wave";
      if (key.includes("update_profile")) return "fal fa-user-edit";
      if (key.includes("reward")) return "fal fa-gift";
      if (key.includes("order")) return "fal fa-shopping-cart";
      return "fal fa-exclamation-circle";
    },

    getActionCardColorClass(action, index) {
      const key = (action.key || "").toLowerCase();
      if (key.includes("cashout")) return "action-color-warning";
      if (key.includes("update_profile")) return "action-color-info";
      if (key.includes("reward")) return "action-color-success";
      const classes = [
        "action-color-purple",
        "action-color-warning",
        "action-color-info",
      ];
      return classes[index % classes.length];
    },

    getStoreName(item) {
      return (
        item.store?.trad_name ||
        item.trad_name ||
        item.name ||
        item.full_name ||
        "متجر"
      );
    },

    getStoreImage(item) {
      return (
        item.store?.store_image ||
        item.store_image ||
        item.avatar ||
        require("@/assets/images/icons/logo.png")
      );
    },

    // ==================== SEARCH & MODAL ====================
    routeToPage(item) {
      if (item.user_type === "driver" && this.canDrivers) {
        this.$router.push(`/driver/show/${item.id}`);
      } else if (item.user_type === "restaurant" && this.canStores) {
        this.$router.push(`/store/show/${item.id}`);
      } else if (item.user_type === "client" && this.canClients) {
        this.$router.push(`/client/show/${item.id}`);
      } else if (this.search_type === "order") {
        const orderType = item.type || item.order_type;
        if (orderType === "package" && this.canPackageOrders) {
          this.$router.push(`/package_order/show/${item.id}`);
        } else if (this.canDeliveryOrders) {
          this.$router.push(`/delivery_orders/show/${item.id}`);
        }
      }
    },

    show_model_1(e) {
      this.model_1.model_img_src = e.target.src;
      this.model_1.show_model = true;
    },

    search() {
      if (this.search_term && this.search_type === "user") {
        this.$axios
          .get(`/statistics/search_users?phone=${this.search_term}`)
          .then((res) => {
            this.search_result = res.data?.data || [];
          })
          .catch(() => {
            this.search_result = [];
          });
      } else if (this.search_term && this.search_type === "order") {
        this.$axios
          .get(`statistics?order_id=${this.search_term}`)
          .then((res) => {
            this.search_result = res.data?.data?.orders || [];
          })
          .catch(() => {
            this.search_result = [];
          });
      } else {
        this.search_result = [];
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      window.dispatchEvent(new Event("resize"));
    });
  },

  created() {
    // Restore date filters from URL query
    const params = new URLSearchParams(window.location.search);
    if (params.get("from")) {
      this.filter_from = params.get("from");
    }
    if (params.get("to")) {
      this.filter_to = params.get("to");
    }
    this.initDashboard();
    if (!this.canDeliveryOrders && this.canPackageOrders) {
      this.activeOrderTab = "package";
    }
  },
};
</script>

<style lang="scss" scoped>
/* ==================== NO PERMISSION VIEW ==================== */
.no-permission-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
}

.no-permission-card {
  background: white;
  border-radius: 28px;
  padding: 48px 36px;
  text-align: center;
  max-width: 620px;
  width: 100%;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);

  .lock-icon-glow {
    position: relative;
    width: 100px;
    height: 100px;
    margin: 0 auto 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    display: flex;
    align-items: center;
    justify-content: center;

    .shield-icon {
      font-size: 3.2rem;
      color: #ef4444;
      opacity: 0.3;
      position: absolute;
    }

    .lock-icon {
      font-size: 2.2rem;
      color: #dc2626;
      position: relative;
      z-index: 2;
    }
  }

  .no-permission-title {
    font-size: 1.8rem;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 12px;
  }

  .no-permission-text {
    font-size: 1rem;
    color: #64748b;
    line-height: 1.7;
    margin-bottom: 0;
  }

  .allowed-modules-section {
    border-top: 1px dashed #e2e8f0;
    padding-top: 24px;

    .allowed-title {
      font-size: 0.95rem;
      font-weight: 700;
      color: #475569;
    }

    .module-chip {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px;
      border-radius: 30px;
      background: #f8fafc;
      color: #783d73;
      text-decoration: none;
      font-weight: 700;
      font-size: 0.9rem;
      border: 1px solid #e2e8f0;
      transition: all 0.3s ease;

      &:hover {
        background: #783d73;
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(120, 61, 115, 0.25);
      }
    }
  }
}

/* ==================== HERO WELCOME BANNER & FILTER ==================== */
.hero-welcome-card {
  position: relative;
  background: linear-gradient(
    135deg,
    var(--mainColor-2, #783d73) 0%,
    #4a2747 100%
  );
  border-radius: 26px;
  padding: 36px 40px;
  overflow: hidden;
  color: white;
  box-shadow: 0 20px 45px rgba(120, 61, 115, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.12);

  .hero-bg-glows {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    overflow: hidden;

    .glow-blob {
      position: absolute;
      border-radius: 50%;
      filter: blur(60px);
      opacity: 0.35;
    }

    .blob-1 {
      width: 280px;
      height: 280px;
      background: var(--secondColor, #c846e3);
      top: -60px;
      left: -60px;
    }

    .blob-2 {
      width: 320px;
      height: 320px;
      background: rgba(255, 255, 255, 0.25);
      bottom: -100px;
      right: 15%;
    }
  }

  .hero-content {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    flex-wrap: wrap;

    .hero-text-side {
      flex: 1;
      min-width: 320px;
    }

    .hero-filter-side {
      flex-shrink: 0;
    }
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(12px);
    padding: 6px 16px;
    border-radius: 30px;
    font-size: 0.88rem;
    font-weight: 600;
    margin-bottom: 14px;

    .pulse-indicator {
      width: 8px;
      height: 8px;
      background-color: #34d399;
      border-radius: 50%;
      box-shadow: 0 0 10px #34d399;
      animation: pulseGlow 2s infinite;
    }
  }

  .hero-title {
    font-size: 2rem;
    font-weight: 800;
    margin-bottom: 10px;
    color: #ffffff;
  }

  .hero-subtitle {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.9);
    max-width: 550px;
    margin-bottom: 24px;
    line-height: 1.6;
  }

  .hero-actions {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;

    .hero-btn {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 12px 24px;
      border-radius: 14px;
      font-weight: 700;
      font-size: 0.95rem;
      border: none;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      &.primary-btn {
        background: #ffffff;
        color: var(--mainColor-2, #783d73);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
        font-weight: 800;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25);
          background: #f8fafc;
        }
      }

      &.secondary-btn {
        background: rgba(255, 255, 255, 0.18);
        color: white;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.3);

        &:hover {
          background: rgba(255, 255, 255, 0.28);
          transform: translateY(-2px);
        }
      }
    }
  }

  /* Filter Glass Box */
  .floating-filter-glass {
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 20px;
    padding: 20px 24px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
    width: 340px;

    .filter-glass-header {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 700;
      font-size: 0.92rem;
      margin-bottom: 14px;
      color: rgba(255, 255, 255, 0.95);
    }

    .filter-inputs-grid {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 14px;

      .filter-field {
        display: flex;
        flex-direction: column;
        gap: 4px;

        label {
          font-size: 0.78rem;
          color: rgba(255, 255, 255, 0.75);
          font-weight: 600;
          margin: 0;
        }

        .date-input {
          background: rgba(255, 255, 255, 0.9);
          color: #1e293b;
          border: 1px solid rgba(255, 255, 255, 0.5);
          border-radius: 10px;
          padding: 8px 12px;
          font-size: 0.85rem;
          font-weight: 600;
          outline: none;
          width: 100%;
          transition: all 0.2s ease;

          &:focus {
            background: #ffffff;
            border-color: #ffffff;
            box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
          }
        }
      }
    }

    .filter-actions-row {
      display: flex;
      align-items: center;
      gap: 8px;

      .apply-filter-btn {
        flex: 1;
        background: #ffffff;
        color: #783d73;
        border: none;
        border-radius: 10px;
        padding: 9px 16px;
        font-weight: 800;
        font-size: 0.88rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          background: #f1f5f9;
          transform: translateY(-1px);
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }

      .reset-filter-btn {
        width: 38px;
        height: 38px;
        border-radius: 10px;
        background: rgba(255, 255, 255, 0.2);
        color: white;
        border: 1px solid rgba(255, 255, 255, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      }
    }
  }
}

/* ==================== PENDING ACTIONS CARDS ==================== */
.pending-action-card-link {
  text-decoration: none;
  display: block;
  color: inherit;

  &.clickable-card {
    cursor: pointer;
  }
}

.pending-action-card {
  background: white;
  border-radius: 18px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  .action-icon-box {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    flex-shrink: 0;
  }

  .action-content-box {
    flex: 1;

    .action-title {
      font-size: 0.95rem;
      font-weight: 700;
      color: #1e293b;
      display: block;
      margin-bottom: 4px;
    }

    .action-meta-row {
      display: flex;
      align-items: center;
      gap: 8px;

      .action-count-pill {
        background: #fef2f2;
        color: #ef4444;
        font-weight: 800;
        font-size: 0.8rem;
        padding: 2px 10px;
        border-radius: 12px;
      }

      .action-status-label {
        font-size: 0.78rem;
        color: #94a3b8;
        font-weight: 600;
      }
    }
  }

  .action-arrow {
    color: #94a3b8;
    font-size: 0.9rem;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);

    .action-arrow {
      transform: translateX(-4px);
      color: #783d73;
    }
  }

  &.action-color-warning {
    border-right: 4px solid #f59e0b;
    .action-icon-box {
      background: rgba(245, 158, 11, 0.1);
      color: #f59e0b;
    }
    .action-count-pill {
      background: rgba(245, 158, 11, 0.12);
      color: #d97706;
    }
  }

  &.action-color-info {
    border-right: 4px solid #06b6d4;
    .action-icon-box {
      background: rgba(6, 182, 212, 0.1);
      color: #06b6d4;
    }
    .action-count-pill {
      background: rgba(6, 182, 212, 0.12);
      color: #0284c7;
    }
  }

  &.action-color-success {
    border-right: 4px solid #10b981;
    .action-icon-box {
      background: rgba(16, 185, 129, 0.1);
      color: #10b981;
    }
    .action-count-pill {
      background: rgba(16, 185, 129, 0.12);
      color: #059669;
    }
  }

  &.action-color-purple {
    border-right: 4px solid #783d73;
    .action-icon-box {
      background: rgba(120, 61, 115, 0.1);
      color: #783d73;
    }
    .action-count-pill {
      background: rgba(120, 61, 115, 0.12);
      color: #783d73;
    }
  }
}

/* ==================== SECTION HEADERS ==================== */
.section-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 10px;

  .title-icon {
    color: #783d73;
    font-size: 1.4rem;

    &.warning-icon {
      color: #f59e0b;
    }
  }
}

.stats-count-badge {
  background: rgba(120, 61, 115, 0.08);
  color: #783d73;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 4px 14px;
  border-radius: 20px;

  &.warning-badge {
    background: rgba(245, 158, 11, 0.1);
    color: #d97706;
  }
}

/* ==================== CREATIVE STAT CARDS ==================== */
.magic-chart-wrapper {
  margin-bottom: 24px;
}

.creative-card-link {
  text-decoration: none;
  display: block;
  height: 100%;

  &.clickable-card {
    cursor: pointer;
  }
}

.creative-stat-card {
  position: relative;
  background: var(--card-bg-gradient, #ffffff);
  background-color: #ffffff;
  border-radius: 22px;
  padding: 22px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.03);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  min-height: 185px;

  .card-ambient-glow {
    position: absolute;
    top: -30px;
    right: -30px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: var(--card-glow-color, rgba(120, 61, 115, 0.15));
    filter: blur(40px);
    opacity: 0.6;
    transition: all 0.5s ease;
    pointer-events: none;
  }

  .card-shimmer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
    transform: translateX(-100%);
    transition: transform 0.8s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-6px) scale(1.015);
    box-shadow: 0 20px 40px var(--card-glow-color, rgba(120, 61, 115, 0.2));
    border-color: var(--card-theme-color, #783d73);

    .card-shimmer {
      transform: translateX(100%);
    }

    .card-ambient-glow {
      transform: scale(1.5);
      opacity: 0.9;
    }

    .icon-glass-box {
      transform: scale(1.1) rotate(6deg);
      background: var(--card-theme-color, #783d73) !important;
      color: white !important;
      box-shadow: 0 10px 20px var(--card-glow-color, rgba(120, 61, 115, 0.3));
    }

    .card-footer-action {
      color: var(--card-theme-color, #783d73);
      .arrow-icon {
        transform: translateX(-6px);
      }
    }
  }
}

.card-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  .icon-glass-box {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    background: var(--card-icon-bg, rgba(120, 61, 115, 0.1));
    color: var(--card-theme-color, #783d73);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    transition: all 0.4s ease;
  }

  .trend-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.8rem;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 20px;

    &.up {
      background: rgba(16, 185, 129, 0.1);
      color: #10b981;
    }

    &.neutral {
      background: rgba(99, 102, 241, 0.1);
      color: #6366f1;
    }
  }
}

.card-metric-row {
  margin-bottom: 12px;

  .stat-number {
    font-size: 2rem;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.2;
    margin: 0 0 4px 0;
    letter-spacing: -0.5px;
  }

  .stat-label-text {
    font-size: 0.95rem;
    color: #64748b;
    font-weight: 600;
    display: block;
  }
}

.card-footer-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.82rem;
  font-weight: 700;
  color: #94a3b8;
  padding-top: 10px;
  border-top: 1px dashed rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;

  .arrow-icon {
    font-size: 0.9rem;
    transition: transform 0.3s ease;
  }
}

/* ==================== GENDER CHARTS ==================== */
.chart-box-card {
  background: white;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.03);
  height: 100%;
  display: flex;
  flex-direction: column;

  .chart-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;

    .chart-title-wrap {
      display: flex;
      align-items: center;
      gap: 12px;

      .chart-icon-box {
        width: 44px;
        height: 44px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25rem;

        &.driver-icon-bg {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        &.order-icon-bg {
          background: rgba(236, 72, 153, 0.1);
          color: #ec4899;
        }
      }

      .chart-title {
        font-size: 1.15rem;
        font-weight: 800;
        color: #1e293b;
        margin: 0;
      }

      .chart-sub {
        font-size: 0.82rem;
        color: #64748b;
        margin: 2px 0 0;
      }
    }

    .total-stat-pill {
      background: #f1f5f9;
      color: #475569;
      font-weight: 700;
      font-size: 0.82rem;
      padding: 5px 14px;
      border-radius: 20px;
    }
  }

  .donut-chart-container {
    min-height: 270px;
  }

  .gender-stats-summary-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 16px;

    .gender-item-card {
      background: #f8fafc;
      border-radius: 14px;
      padding: 12px;
      text-align: center;
      border: 1px solid #e2e8f0;

      .gender-key-badge {
        font-size: 0.82rem;
        font-weight: 700;
        display: block;
        margin-bottom: 4px;

        i {
          margin-left: 4px;
        }
      }

      .gender-count {
        font-size: 1.25rem;
        font-weight: 800;
        color: #0f172a;
      }

      &.male {
        .gender-key-badge {
          color: #3b82f6;
        }
      }

      &.female {
        .gender-key-badge {
          color: #ec4899;
        }
      }

      &.unknown {
        .gender-key-badge {
          color: #64748b;
        }
      }
    }
  }
}

/* ==================== TOP TABLES TABS ==================== */
.top-tabs-header {
  margin-bottom: 24px;
}

.top-tabs-nav {
  display: flex;
  gap: 8px;
  border-bottom: 2px solid #f1f5f9;
  padding-bottom: 0;
}

.top-tab-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  transition: all 0.3s ease;
  border-radius: 12px 12px 0 0;

  &:hover {
    background: rgba(120, 61, 115, 0.04);
  }

  &.active {
    border-bottom-color: #783d73;
    background: rgba(120, 61, 115, 0.06);

    .top-tab-label {
      color: #1e293b;
      font-weight: 700;
    }

    .top-tab-icon {
      transform: scale(1.05);
    }

    .store-tab-icon {
      background: rgba(16, 185, 129, 0.15);
      color: #10b981;
    }
    .client-tab-icon {
      background: rgba(120, 61, 115, 0.15);
      color: #783d73;
    }
    .driver-tab-icon {
      background: rgba(245, 158, 11, 0.15);
      color: #f59e0b;
    }
    .delivery-tab-icon {
      background: rgba(120, 61, 115, 0.15);
      color: #783d73;
    }
    .package-tab-icon {
      background: rgba(2, 132, 199, 0.15);
      color: #0284c7;
    }
  }
}

.top-tab-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  transition: all 0.3s ease;

  &.store-tab-icon {
    background: rgba(16, 185, 129, 0.08);
    color: #10b981;
  }
  &.client-tab-icon {
    background: rgba(120, 61, 115, 0.08);
    color: #783d73;
  }
  &.driver-tab-icon {
    background: rgba(245, 158, 11, 0.08);
    color: #f59e0b;
  }
  &.delivery-tab-icon {
    background: rgba(120, 61, 115, 0.08);
    color: #783d73;
  }
  &.package-tab-icon {
    background: rgba(2, 132, 199, 0.08);
    color: #0284c7;
  }
}

.phone-cell {
  font-family: inherit;
  direction: ltr;
  display: inline-flex;
  align-items: center;
  color: #334155;
  font-weight: 500;
  font-size: 0.875rem;

  i {
    color: #94a3b8;
  }
}

.top-tab-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.top-tab-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #64748b;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.top-tab-count {
  background: linear-gradient(135deg, #783d73, #a855a0);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  min-width: 24px;
  text-align: center;
  line-height: 1.4;
}

.top-tab-content {
  animation: tabFadeIn 0.3s ease;
}

@keyframes tabFadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ==================== CREATIVE TABLE CARD ==================== */
.creative-table-card {
  background: white;
  border-radius: 24px;
  padding: 28px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.table-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  .table-header-title {
    display: flex;
    align-items: center;
    gap: 16px;

    .header-icon-wrap {
      width: 48px;
      height: 48px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.4rem;

      &.orders-header-icon {
        background: rgba(99, 102, 241, 0.1);
        color: #6366f1;
      }

      &.store-header-icon {
        background: rgba(16, 185, 129, 0.1);
        color: #10b981;
      }

      &.client-header-icon {
        background: rgba(120, 61, 115, 0.1);
        color: #783d73;
      }

      &.driver-header-icon {
        background: rgba(245, 158, 11, 0.1);
        color: #f59e0b;
      }
    }

    .table-title {
      font-size: 1.35rem;
      font-weight: 800;
      color: #1e293b;
      margin: 0;
    }

    .table-subtitle {
      font-size: 0.88rem;
      color: #64748b;
      margin: 2px 0 0 0;
    }
  }

  .table-header-badge {
    background: rgba(120, 61, 115, 0.08);
    color: #783d73;
    font-weight: 700;
    font-size: 0.85rem;
    padding: 6px 16px;
    border-radius: 30px;
  }
}

/* Custom Table Elements */
.order-number-badge {
  font-weight: 800;
  color: #1e293b;
  font-size: 0.95rem;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 10px;
}

.order-type-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;

  &.type-delivery {
    background: rgba(59, 130, 246, 0.1);
    color: #2563eb;
  }

  &.type-package {
    background: rgba(168, 85, 247, 0.1);
    color: #9333ea;
  }
}

/* ==================== ADDITIONAL DETAILS PILLS ==================== */
.additional-details-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}

.detail-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;

  i {
    font-size: 0.72rem;
  }

  &.pill-fulfillment {
    background: rgba(245, 158, 11, 0.12);
    color: #d97706;
  }

  &.pill-platform {
    background: rgba(16, 185, 129, 0.12);
    color: #059669;
  }

  &.pill-ondemand {
    background: rgba(59, 130, 246, 0.12);
    color: #2563eb;
  }

  &.pill-default {
    background: rgba(100, 116, 139, 0.1);
    color: #64748b;
  }
}

.store-cell-item {
  display: flex;
  align-items: center;
  gap: 12px;

  .store-img-wrapper {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
    border: 2px solid white;
    cursor: pointer;

    .store-avatar {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.1);
      }
    }
  }

  .store-name {
    font-weight: 700;
    color: #1e293b;
    font-size: 0.92rem;
  }
}

.stat-count-chip {
  background: #f1f5f9;
  color: #334155;
  font-weight: 800;
  font-size: 0.88rem;
  padding: 4px 12px;
  border-radius: 12px;
}

.rating-pill {
  background: #fef3c7;
  color: #92400e;
  font-weight: 700;
  font-size: 0.85rem;
  padding: 3px 10px;
  border-radius: 14px;
}

.index-badge {
  font-weight: 800;
  color: #94a3b8;
  font-size: 0.88rem;
}

.creative-status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;

  &.status-active {
    background: rgba(16, 185, 129, 0.1);
    color: #059669;

    .dot-indicator {
      width: 7px;
      height: 7px;
      background: #10b981;
      border-radius: 50%;
    }
  }

  &.status-inactive {
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;

    .dot-indicator {
      width: 7px;
      height: 7px;
      background: #ef4444;
      border-radius: 50%;
    }
  }
}

.table-actions-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .action-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    transition: all 0.2s ease;

    &.view-btn {
      background: rgba(120, 61, 115, 0.08);
      color: #783d73;

      &:hover {
        background: #783d73;
        color: white;
        transform: translateY(-2px);
      }
    }
  }

  .action-btn-disabled {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f5f9;
    color: #cbd5e1;
    cursor: not-allowed;
  }
}

.empty-table-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  color: #94a3b8;

  .empty-icon {
    font-size: 2.5rem;
    margin-bottom: 12px;
  }
}

.direction-ltr {
  direction: ltr;
}

/* ==================== OVERALL EMPTY DASHBOARD ==================== */
.empty-dashboard-container {
  background: white;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  padding: 60px 24px;

  .empty-dash-icon {
    font-size: 3.5rem;
    color: #cbd5e1;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.4rem;
    font-weight: 800;
    color: #1e293b;
    margin-bottom: 8px;
  }

  p {
    max-width: 480px;
    margin: 0 auto;
    font-size: 0.95rem;
  }
}

/* ==================== SEARCH MODAL ==================== */
.search-modal-card {
  border-radius: 20px !important;
  overflow: hidden;
  &.v-card {
    padding: 0 !important;
  }
}

.modal-header-strip {
  background: linear-gradient(135deg, #783d73 0%, #5a2c56 100%);
  color: white;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h3 {
    font-size: 1.15rem;
    font-weight: 700;
    margin: 0;
  }

  .close-modal-btn {
    color: white !important;
  }
}

.selector-label {
  font-weight: 700;
  font-size: 0.9rem;
  color: #334155;
  margin-bottom: 6px;
  display: block;
}

.search-input-wrap {
  position: relative;

  .search-input-icon {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 1.1rem;
  }

  .search_home_input_creative {
    width: 100%;
    padding: 14px 48px 14px 16px;
    border-radius: 14px;
    border: 2px solid #e2e8f0;
    outline: none;
    font-size: 1rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #783d73;
      box-shadow: 0 0 0 4px rgba(120, 61, 115, 0.1);
    }
  }
}

.empty-search {
  color: #94a3b8;

  .search-empty-icon {
    font-size: 2.2rem;
  }
}

.user-type-tag {
  background: #f1f5f9;
  color: #475569;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 4px 12px;
  border-radius: 12px;
}

@keyframes pulseGlow {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}
</style>
